import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { hasActiveCommerceHubEnterpriseTrial, hasActiveCommerceHubProTrial, hasActiveContentHubEnterpriseTrial, hasActiveContentHubProTrial, hasActiveEnterpriseTrial, hasActiveMarketingEnterprisePlusTrial, hasActiveMarketingHubEnterpriseTrial, hasActiveMarketingHubProTrial, hasActiveMarketingProPlusTrial, hasActiveOpsHubEnterpriseTrial, hasActiveOpsHubProTrial, hasActiveProTrial, hasActiveSalesHubEnterpriseTrial, hasActiveSalesHubProTrial, hasActiveServiceHubEnterpriseTrial, hasActiveServiceHubProTrial, hasActiveTrial, hasOnlyActiveEnterpriseTrial, hasOnlyActiveProTrial } from './hasActiveTrials';
import { isOwned, ownsAll, ownsAny, ownsNone } from './ownershipUtils';
import { getOwnsCmsHubEnterprise, getOwnsCmsHubPro, getOwnsCmsHubStarter, getOwnsCommerceHubEnterprise, getOwnsCommerceHubPro, getOwnsMarketingHubEnterprise, getOwnsMarketingHubPro, getOwnsMarketingHubStarter, getOwnsOpsHubEnterprise, getOwnsOpsHubPro, getOwnsOpsHubStarter, getOwnsSalesHubEnterprise, getOwnsSalesHubPro, getOwnsSalesHubStarter, getOwnsServiceHubEnterprise, getOwnsServiceHubPro, getOwnsServiceHubStarter } from './ownsHubs';
import { getOwnsCrmSuiteStarter } from './ownsSuite';
import { getIsFreePortal, getOwnsEnterprisePortal, getOwnsFreeOrStarterPortal, getOwnsOnlyEnterprisePortal, getOwnsOnlyProPortal, getOwnsOnlyStarterPortal, getOwnsPaidPortal, getOwnsProPlusPortal, getOwnsProPortal, getOwnsStarterPortal } from './ownsTier';
export const useScopes = () => useSelector(state => {
  const scopes = state.auth && state.auth.user && state.auth.user.scopes;
  if (!scopes) {
    console.warn(`Onboarding Scopes: No Scopes present in state, this will cause unexpected behaviour in identifying Hubs.
        Is this hook used within a Redux context? Does the Redux context store scopes at 'auth.user.scopes'?`, {
      state
    });
    return [];
  }
  return scopes;
});

// Active Trial Hooks
export const useHasActiveTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveTrial(scopes), [scopes]);
};
export const useHasActiveProTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveProTrial(scopes), [scopes]);
};
export const useHasOnlyActiveProTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasOnlyActiveProTrial(scopes), [scopes]);
};
export const useHasActiveEnterpriseTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveEnterpriseTrial(scopes), [scopes]);
};
export const useHasOnlyActiveEnterpriseTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasOnlyActiveEnterpriseTrial(scopes), [scopes]);
};
export const useHasActiveMarketingHubProTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveMarketingHubProTrial(scopes), [scopes]);
};
export const useHasActiveMarketingHubEnterpriseTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveMarketingHubEnterpriseTrial(scopes), [scopes]);
};
export const useHasActiveContentHubProTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveContentHubProTrial(scopes), [scopes]);
};
export const useHasActiveContentHubEnterpriseTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveContentHubEnterpriseTrial(scopes), [scopes]);
};
export const useHasActiveSalesHubProTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveSalesHubProTrial(scopes), [scopes]);
};
export const useHasActiveSalesHubEnterpriseTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveSalesHubEnterpriseTrial(scopes), [scopes]);
};
export const useHasActiveServiceHubProTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveServiceHubProTrial(scopes), [scopes]);
};
export const useHasActiveServiceHubEnterpriseTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveServiceHubEnterpriseTrial(scopes), [scopes]);
};
export const useHasActiveOpsHubProTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveOpsHubProTrial(scopes), [scopes]);
};
export const useHasActiveOpsHubEnterpriseTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveOpsHubEnterpriseTrial(scopes), [scopes]);
};
export const useHasActiveMarketingProPlusTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveMarketingProPlusTrial(scopes), [scopes]);
};
export const useHasActiveMarketingEnterprisePlusTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveMarketingEnterprisePlusTrial(scopes), [scopes]);
};
export const useHasActiveCommerceHubProTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveCommerceHubProTrial(scopes), [scopes]);
};
export const useHasActiveCommerceHubEnterpriseTrial = () => {
  const scopes = useScopes();
  return useMemo(() => hasActiveCommerceHubEnterpriseTrial(scopes), [scopes]);
};

// Ownership Utility Hooks
export const useIsOwned = scopeToCheck => {
  const scopes = useScopes();
  return useMemo(() => isOwned(scopes, scopeToCheck), [scopes, scopeToCheck]);
};
export const useOwnsAll = candidateScopes => {
  const scopes = useScopes();
  return useMemo(() => ownsAll(scopes, candidateScopes), [scopes, candidateScopes]);
};
export const useOwnsNone = candidateScopes => {
  const scopes = useScopes();
  return useMemo(() => ownsNone(scopes, candidateScopes), [scopes, candidateScopes]);
};
export const useOwnsAny = candidateScopes => {
  const scopes = useScopes();
  return useMemo(() => ownsAny(scopes, candidateScopes), [scopes, candidateScopes]);
};

// Owns Hubs Hooks
export const useOwnsCmsHubEnterprise = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsCmsHubEnterprise(scopes), [scopes]);
};
export const useOwnsCmsHubPro = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsCmsHubPro(scopes), [scopes]);
};
export const useOwnsCmsHubStarter = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsCmsHubStarter(scopes), [scopes]);
};
export const useOwnsMarketingHubEnterprise = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsMarketingHubEnterprise(scopes), [scopes]);
};
export const useOwnsMarketingHubPro = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsMarketingHubPro(scopes), [scopes]);
};
export const useOwnsMarketingHubStarter = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsMarketingHubStarter(scopes), [scopes]);
};
export const useOwnsOpsHubEnterprise = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsOpsHubEnterprise(scopes), [scopes]);
};
export const useOwnsOpsHubPro = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsOpsHubPro(scopes), [scopes]);
};
export const useOwnsOpsHubStarter = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsOpsHubStarter(scopes), [scopes]);
};
export const useOwnsSalesHubEnterprise = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsSalesHubEnterprise(scopes), [scopes]);
};
export const useOwnsSalesHubPro = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsSalesHubPro(scopes), [scopes]);
};
export const useOwnsSalesHubStarter = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsSalesHubStarter(scopes), [scopes]);
};
export const useOwnsServiceHubEnterprise = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsServiceHubEnterprise(scopes), [scopes]);
};
export const useOwnsServiceHubPro = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsServiceHubPro(scopes), [scopes]);
};
export const useOwnsServiceHubStarter = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsServiceHubStarter(scopes), [scopes]);
};
export const useOwnsCommerceHubEnterprise = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsCommerceHubEnterprise(scopes), [scopes]);
};
export const useOwnsCommerceHubPro = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsCommerceHubPro(scopes), [scopes]);
};

// Owns Suite Hooks
export const useOwnsCrmSuiteStarter = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsCrmSuiteStarter(scopes), [scopes]);
};

// Owns Tier Hooks
export const useIsFreePortal = () => {
  const scopes = useScopes();
  return useMemo(() => getIsFreePortal(scopes), [scopes]);
};
export const useOwnsPaidPortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsPaidPortal(scopes), [scopes]);
};
export const useOwnsOnlyStarterPortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsOnlyStarterPortal(scopes), [scopes]);
};
export const useOwnsStarterPortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsStarterPortal(scopes), [scopes]);
};
export const useOwnsFreeOrStarterPortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsFreeOrStarterPortal(scopes), [scopes]);
};
export const useOwnsOnlyProPortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsOnlyProPortal(scopes), [scopes]);
};
export const useOwnsProPortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsProPortal(scopes), [scopes]);
};
export const useOwnsProPlusPortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsProPlusPortal(scopes), [scopes]);
};
export const useOwnsOnlyEnterprisePortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsOnlyEnterprisePortal(scopes), [scopes]);
};
export const useOwnsEnterprisePortal = () => {
  const scopes = useScopes();
  return useMemo(() => getOwnsEnterprisePortal(scopes), [scopes]);
};